import type { IImage } from "@designcombo/types";
import axios from "axios";
import { imageSize } from "image-size";
import { isDefaultWatermarkUrl } from ".";
import { generateId } from "../trackId";

export type ImageDetailsWithLayout = IImage["details"] & {
	zIndex?: number;
	blendMode?: string;
	border?: string;
	borderRadius?: number;
	borderWidth?: number;
	borderColor?: string;
	boxShadow?: {
		color?: string;
		x?: number;
		y?: number;
		blur?: number;
	};
	visibility?: string;
};

export interface CreateImageDetailsParams {
	src?: string;
	width?: number | string;
	height?: number | string;
	opacity?: number;
	top?: string | number | null;
	left?: string | number | null;
	zIndex?: number;
	overrides?: Partial<ImageDetailsWithLayout>;
}

export interface CreateImageTrackItemParams extends CreateImageDetailsParams {
	display?: {
		from?: number;
		to?: number;
	};
	duration?: number;
	name?: string;
	metadata?: IImage["metadata"];
	id?: string;
	playbackRate?: number;
}

export async function getImageDimensionsForWidth(
	_url: string,
	_targetWidth?: number | string,
): Promise<{
	src: string;
	width?: number;
	height?: number;
	originalWidth: number;
	originalHeight: number;
}> {
	if (!_url) {
		throw new Error("Image URL is required");
	}
	const url = isDefaultWatermarkUrl(_url) ? `${process.env.NEXT_PUBLIC_EDITOR_SITE_URL}/${_url}` : _url;
	const response = await axios.get<ArrayBuffer>(url, {
		responseType: "arraybuffer",
		headers: {
			"Cache-Control": "no-cache, no-store, must-revalidate",
			Pragma: "no-cache",
			Expires: "0",
		},
	});

	const dimensions = imageSize(Buffer.from(response.data));

	if (!dimensions.width || !dimensions.height) {
		throw new Error("Unable to determine image dimensions");
	}

	let scaledHeight = dimensions.height;
	let targetWidth = _targetWidth;
	if (_targetWidth) {
		targetWidth = Number.parseFloat(_targetWidth as string);
		const scale = targetWidth / dimensions.width;
		scaledHeight = Math.round(dimensions.height * scale);
	}

	return {
		src: url,
		width: targetWidth as number,
		height: scaledHeight,
		originalWidth: dimensions.width,
		originalHeight: dimensions.height,
	};
}

export interface ScaleToTargetSizeParams {
	width: number;
	height?: number;
	targetWidth?: number;
	targetHeight?: number;
	tolerance?: number;
}

export function getScaleForTargetSize({
	width,
	height,
	targetWidth,
	targetHeight,
	tolerance = 0.01,
}: ScaleToTargetSizeParams): string {
	if (!Number.isFinite(width) || width <= 0) {
		throw new Error("Source width must be a positive number");
	}

	if (height !== undefined && height !== null) {
		if (!Number.isFinite(height) || height <= 0) {
			throw new Error("Source height must be a positive number when provided");
		}
	}

	let scaleX: number | undefined;
	let scaleY: number | undefined;

	if (targetWidth !== undefined && targetWidth !== null) {
		if (!Number.isFinite(targetWidth) || targetWidth <= 0) {
			throw new Error("Target width must be a positive number when provided");
		}
		scaleX = targetWidth / width;
	}

	if (targetHeight !== undefined && targetHeight !== null) {
		if (height === undefined || height === null) {
			throw new Error("Source height is required when target height is provided");
		}
		if (!Number.isFinite(targetHeight) || targetHeight <= 0) {
			throw new Error("Target height must be a positive number when provided");
		}
		scaleY = targetHeight / height;
	}

	if (scaleX === undefined && scaleY === undefined) {
		return "1";
	}

	if (scaleX === undefined) {
		scaleX = scaleY as number;
	}

	if (scaleY === undefined) {
		scaleY = scaleX;
	}

	if (Math.abs(scaleX - scaleY) <= tolerance) {
		const average = (scaleX + scaleY) / 2;
		return average.toString();
	}

	return `${scaleX}, ${scaleY}`;
}

export interface UniformContainLayout {
	width: number;
	height: number;
	top: string;
	left: string;
	/** Uniform scale factor min(canvasW/iw, canvasH/ih), same as CSS object-fit: contain. */
	scale: number;
}

/**
 * Fits intrinsic media inside the canvas (uniform scale, centered), same idea as
 * {@link getScaleForTargetSize} + watermark positioning: measure source size, scale to scene.
 */
export function computeUniformContainLayout(
	canvasW: number,
	canvasH: number,
	intrinsicW: number,
	intrinsicH: number,
): UniformContainLayout {
	if (
		!Number.isFinite(canvasW) ||
		!Number.isFinite(canvasH) ||
		canvasW <= 0 ||
		canvasH <= 0
	) {
		return {
			width: canvasW,
			height: canvasH,
			top: "0px",
			left: "0px",
			scale: 1,
		};
	}
	if (
		!Number.isFinite(intrinsicW) ||
		!Number.isFinite(intrinsicH) ||
		intrinsicW <= 0 ||
		intrinsicH <= 0
	) {
		return {
			width: canvasW,
			height: canvasH,
			top: "0px",
			left: "0px",
			scale: 1,
		};
	}
	const scale = Math.min(canvasW / intrinsicW, canvasH / intrinsicH);
	const w = Math.round(intrinsicW * scale);
	const h = Math.round(intrinsicH * scale);
	const left = Math.round((canvasW - w) / 2);
	const top = Math.round((canvasH - h) / 2);
	return {
		width: w,
		height: h,
		top: `${top}px`,
		left: `${left}px`,
		scale,
	};
}

export interface UniformCoverLayout {
	/** max(canvasW/iw, canvasH/ih) — uniform scale to fill the frame (CSS object-fit: cover). */
	scale: number;
	scaledWidth: number;
	scaledHeight: number;
}

/**
 * Scales intrinsic media so it **covers** the full canvas (uniform scale, centered), same as
 * CSS `object-fit: cover`: the shorter axis matches the canvas and the extra bleeds past edges
 * (clipped by the layer overflow). Use this for B-roll full-frame; use {@link computeUniformContainLayout} when letterboxing is desired.
 */
export function computeUniformCoverLayout(
	canvasW: number,
	canvasH: number,
	intrinsicW: number,
	intrinsicH: number,
): UniformCoverLayout {
	if (
		!Number.isFinite(canvasW) ||
		!Number.isFinite(canvasH) ||
		canvasW <= 0 ||
		canvasH <= 0
	) {
		return { scale: 1, scaledWidth: canvasW, scaledHeight: canvasH };
	}
	if (
		!Number.isFinite(intrinsicW) ||
		!Number.isFinite(intrinsicH) ||
		intrinsicW <= 0 ||
		intrinsicH <= 0
	) {
		return { scale: 1, scaledWidth: canvasW, scaledHeight: canvasH };
	}
	const scale = Math.max(canvasW / intrinsicW, canvasH / intrinsicH);
	return {
		scale,
		scaledWidth: Math.round(intrinsicW * scale),
		scaledHeight: Math.round(intrinsicH * scale),
	};
}

const DEFAULT_BOX_SHADOW = {
	color: "#000000",
	x: 0,
	y: 0,
	blur: 0,
};

function sanitizeOpacity(value: number | undefined): number {
	if (value === undefined || Number.isNaN(Number(value))) {
		return 100;
	}

	const numeric = Number(value);
	if (!Number.isFinite(numeric)) {
		return 100;
	}

	return Math.min(100, Math.max(0, numeric));
}

function ensurePixelValue(value: string | number | null | undefined, fallback = "0px"): string {
	if (value === undefined || value === null || value === "") {
		return fallback;
	}

	if (typeof value === "number" && Number.isFinite(value)) {
		return `${value}px`;
	}

	return value as string;
}

function coerceDimension(value?: number | string | null): number | undefined {
	if (value === undefined || value === null || value === "") {
		return undefined;
	}

	if (typeof value === "number" && Number.isFinite(value)) {
		return value;
	}

	const parsed = Number.parseFloat(String(value));
	return Number.isFinite(parsed) ? parsed : undefined;
}

export function createImageDetails({
	src,
	width,
	height,
	opacity = 100,
	top,
	left,
	zIndex = 0,
	overrides,
}: CreateImageDetailsParams): ImageDetailsWithLayout {
	const baseDetails: Partial<ImageDetailsWithLayout> = {
		src: src ?? "",
		width: coerceDimension(width) ?? 0,
		height: coerceDimension(height) ?? 0,
		opacity: sanitizeOpacity(opacity),
		transform: "scale(1)",
		border: "none",
		borderRadius: 0,
		boxShadow: DEFAULT_BOX_SHADOW,
		top: ensurePixelValue(top, "0px"),
		left: ensurePixelValue(left, "0px"),
		borderWidth: 0,
		borderColor: "#000000",
		blur: 0,
		brightness: 100,
		flipX: false,
		flipY: false,
		rotate: "0deg",
		visibility: "visible",
	};

	const mergedDetails: Partial<ImageDetailsWithLayout> = {
		...baseDetails,
		...(overrides ?? {}),
		boxShadow: {
			...DEFAULT_BOX_SHADOW,
			...(overrides?.boxShadow ?? {}),
		},
		zIndex: overrides?.zIndex ?? zIndex,
	};

	return mergedDetails as ImageDetailsWithLayout;
}

export function createImageTrackItem({
	src,
	width,
	height,
	opacity,
	top,
	left,
	zIndex,
	overrides,
	display,
	duration,
	name = "image",
	metadata,
	id,
	playbackRate = 1,
}: CreateImageTrackItemParams): IImage {
	const details = createImageDetails({
		src,
		width,
		height,
		opacity,
		top,
		left,
		zIndex,
		overrides,
	});

	const from = display?.from ?? 0;
	const to = display?.to ?? duration ?? 0;

	return {
		id: id ?? generateId(10),
		name,
		type: "image",
		playbackRate,
		display: {
			from,
			to,
		},
		duration: duration ?? Math.max(0, to - from),
		details: details as any,
		metadata: metadata ?? {},
	};
}
